/**
 * --------------------------------------------------------------------------
 * Bootstrap (v5.1.3): util/focustrap.js
 * Licensed under MIT (https://github.com/twbs/bootstrap/blob/main/LICENSE)
 * --------------------------------------------------------------------------
 */

import EventHandler from 'bootstrap/js/src/dom/event-handler';
// import SelectorEngine from 'bootstrap/js/src/dom/selector-engine';
import { typeCheckConfig } from 'bootstrap/js/src/util';
import { getFocusableChildren, isFocusable } from '../../util/dom.js';

/**
 * @typedef {{ trapElement?: HTMLElement, autofocus?: boolean|string }} FocusTrapOptions
 */
const Default = {
	trapElement: null, // The element to trap focus inside of
	autofocus: true,
};

const DefaultType = {
	trapElement: 'element',
	autofocus: '(boolean|string)',
};

const NAME = 'focustrap';
const DATA_KEY = 'bs.focustrap';
const EVENT_KEY = `.${DATA_KEY}`;
const EVENT_FOCUSIN = `focusin${EVENT_KEY}`;
const EVENT_KEYDOWN_TAB = `keydown.tab${EVENT_KEY}`;

const TAB_KEY = 'Tab';
const TAB_NAV_FORWARD = 'forward';
const TAB_NAV_BACKWARD = 'backward';

class FocusTrap {

	/**
	 * @param {FocusTrapOptions} config
	 */
	constructor(config) {
		this._config = this._getConfig(config);
		this._isActive = false;
		this._lastTabNavDirection = null;
	}

	activate() {
		const { trapElement, autofocus } = this._config;

		// Autofocus component
		if (autofocus === true && isFocusable(trapElement)) {
			trapElement.focus();
		}
		// Autofocus selector string
		else if (autofocus) {
			this.focusFirstFocusable(typeof autofocus === 'string' ? autofocus : null);
		}

		// Event already added
		if (this._isActive) {
			return;
		}

		EventHandler.off(document, EVENT_KEY); // guard against infinite focus loop
		EventHandler.on(document, EVENT_FOCUSIN, event => this._handleFocusin(event));
		EventHandler.on(document, EVENT_KEYDOWN_TAB, event => this._handleKeydown(event));

		this._isActive = true;
	}

	deactivate() {
		if (!this._isActive) {
			return;
		}

		this._isActive = false;
		EventHandler.off(document, EVENT_KEY);
	}

	/**
	 * @param {string=} filterSelector
	 */
	focusFirstFocusable(filterSelector) {
		const { trapElement } = this._config;
		const focusable = getFocusableChildren(trapElement, filterSelector)[0];

		if (focusable) {
			focusable.focus();
		}

		return this;
	}

	// Private

	_handleFocusin(event) {
		const { target } = event;
		const { trapElement } = this._config;

		if (target === document || target === trapElement || trapElement.contains(target)) {
			return;
		}

		// Predefined excluded element selectors
		const excluded = mpro.config.ui.focustrap.excluded_elements;
		if (excluded && (target.matches(excluded) || target.closest(excluded))) {
			return;
		}

		const elements = getFocusableChildren(trapElement);

		if (elements.length === 0) {
			trapElement.focus();
		}
		else if (this._lastTabNavDirection === TAB_NAV_BACKWARD) {
			elements.at(-1).focus();
		}
		else {
			elements[0].focus();
		}
	}

	_handleKeydown(event) {
		if (event.key !== TAB_KEY) {
			return;
		}

		this._lastTabNavDirection = event.shiftKey ? TAB_NAV_BACKWARD : TAB_NAV_FORWARD;
	}

	/**
	 * @param {FocusTrapOptions} config
	 * @returns {FocusTrapOptions}
	 */
	_getConfig(config) {
		config = {
			...Default,
			...(typeof config === 'object' ? config : {}),
		};
		typeCheckConfig(NAME, config, DefaultType);
		return config;
	}
}

export default FocusTrap;
